document.addEventListener('rebuy.loaded', (event) => {

  /* ---------------------------------------------------------------------------
   * Premier Catch Smart Cart extensions (v2)
   * One eligibility rule for "frozen seafood" drives both the $125 minimum check
   * and the shipping progress bar, so the cart never disagrees with checkout.
   * Frozen = anything not matching the excluded types/tags below. These match
   * the Shopify delivery profiles: excluded items ship in Non Frozen Shipping
   * (canned tuna, merch, physical gift cards) or don't ship (digital items).
   * ------------------------------------------------------------------------- */
  // Thresholds (cents) come from theme settings via window.PC_CART, emitted in
  // layout/theme.liquid; fall back to the original values if missing/invalid.
  const PC_CART = window.PC_CART || {};
  const centsOr = (v, d) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.round(Number(v)) : d);
  const MINIMUM = centsOr(PC_CART.minimum, 12500);           // frozen seafood minimum
  const FREE_SHIPPING = centsOr(PC_CART.freeShipping, 17500); // free standard shipping
  const STANDARD_FEE = centsOr(PC_CART.shippingFee, 2900);    // standard shipping below free threshold
  const EXCLUDED_TYPES = ['Gift Card', 'Ebook', 'Canned Fish', 'Merch'];
  const EXCLUDED_TAGS = ['No Minimum', 'preorder', 'smart-cart-shipping-excluded', 'Gift Card'];
  const RECS_TITLE = 'Add More Wild Seafood';

  // Built lazily (money() is defined below).
  const labelOneTime = () => 'Minimum Order of ' + money(MINIMUM) + ' on Frozen Seafood Required for Checkout.';
  const subscriptionShortLabel = (shortCents) =>
    'Add ' + money(shortCents) + ' more in subscription seafood to continue to checkout.';

  let checkoutBtn = null;
  let accordionToggle = null;
  let popUpTriggerBtn = null;
  let lastCart = null;
  let observer = null;
  let ensureQueued = false;

  /* ---------- eligibility + totals ---------- */

  const lineTags = (lineItem) => {
    const raw = lineItem.product && lineItem.product.tags;
    if (Array.isArray(raw)) return raw;
    return String(raw || '').split(',').map(t => t.trim()).filter(Boolean);
  };

  const isFrozenLine = (lineItem) => {
    if (lineItem.gift_card) return false;
    if (EXCLUDED_TYPES.includes(lineItem.product_type)) return false;
    const tags = lineTags(lineItem);
    return !tags.some(t => EXCLUDED_TAGS.includes(t));
  };

  const isSubscriptionLine = (lineItem) =>
    !!(lineItem.selling_plan_allocation && lineItem.selling_plan_allocation.selling_plan);

  // Returns cents. Uses each line's discounted price (subscription savings and
  // automatic product discounts), then removes a proportional share of any
  // order-level discount so the total matches what Shopify charges shipping on.
  const getTotals = (cart) => {
    let frozen = 0, frozenSubscription = 0, allLines = 0, hasSubscription = false;
    (cart && cart.items || []).forEach(lineItem => {
      const price = Number(lineItem.final_line_price) || 0;
      allLines += price;
      if (!isFrozenLine(lineItem)) return;
      frozen += price;
      if (isSubscriptionLine(lineItem)) {
        frozenSubscription += price;
        hasSubscription = true;
      }
    });
    let orderDiscount = 0;
    (cart && cart.cart_level_discount_applications || []).forEach(app => {
      orderDiscount += Number(app.total_allocated_amount) || 0;
    });
    if (orderDiscount > 0 && allLines > 0 && frozen > 0) {
      const share = frozen / allLines;
      frozen = Math.max(0, frozen - Math.round(orderDiscount * share));
      frozenSubscription = Math.max(0, frozenSubscription - Math.round(orderDiscount * (frozenSubscription / allLines)));
    }
    return { frozen, frozenSubscription, hasSubscription };
  };

  const money = (cents) => {
    const dollars = cents / 100;
    return '$' + (Math.abs(dollars - Math.round(dollars)) < 0.005 ? String(Math.round(dollars)) : dollars.toFixed(2));
  };

  /* ---------- checkout button / minimum ---------- */

  const findCheckoutBtn = () => {
    checkoutBtn = document.querySelector('.rebuy-button.rebuy-cart__checkout-button');
  };

  const makeNewCheckoutBtn = () => {
    if (popUpTriggerBtn == null || !document.body.contains(popUpTriggerBtn)) {
      const newButton = document.createElement("button");
      newButton.className = "button subscription-warning";
      newButton.type = "button";
      newButton.innerHTML = '<span class="subscription-warning__text"></span>';
      popUpTriggerBtn = checkoutBtn.insertAdjacentElement("afterend", newButton);
      newButton.addEventListener("click", () => {
        if (typeof window.openPopup === 'function') { window.openPopup(); return; }
        const acc = document.querySelector('.rebuy-cart .amb-accordion');
        if (acc) { acc.classList.add('open'); acc.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
      });
    }
  };

  const setWarning = (label) => {
    makeNewCheckoutBtn();
    const textEl = popUpTriggerBtn.querySelector('.subscription-warning__text');
    if (textEl && textEl.textContent !== label) textEl.textContent = label;
    checkoutBtn.style.display = "none";
    popUpTriggerBtn.style.display = "block";
  };

  const updateCheckoutBtn = (cart) => {
    if (!cart) return;
    lastCart = cart;
    const totals = getTotals(cart);

    // Display final (discounted) line price on each flyout line.
    // Rebuy's flyout DOM exposes no line key/ID attribute, but renders one
    // .rebuy-cart__flyout-item per cart line in cart.items order. Map by line
    // index (i.e. per line key), and only when the DOM matches the cart exactly
    // (same count, and each element carries that line's .product-<handle>);
    // otherwise skip rather than write a price onto the wrong line.
    const items = cart.items || [];
    const lineEls = document.querySelectorAll('.rebuy-cart__flyout-items .rebuy-cart__flyout-item');
    if (lineEls.length === items.length) {
      const aligned = items.every((lineItem, i) => lineEls[i].classList.contains('product-' + lineItem.handle));
      if (aligned) {
        items.forEach((lineItem, i) => {
          const linePriceEl = lineEls[i].querySelector('.rebuy-money span:nth-child(2)');
          if (linePriceEl != null) {
            linePriceEl.innerHTML = "$" + (lineItem.final_line_price / 100).toFixed(2);
          }
        });
      }
    }

    findCheckoutBtn();
    if (checkoutBtn != null) {
      if (totals.hasSubscription && totals.frozenSubscription > 0 && totals.frozenSubscription < MINIMUM) {
        setWarning(subscriptionShortLabel(MINIMUM - totals.frozenSubscription));
      } else if (totals.frozen > 0 && totals.frozen < MINIMUM) {
        setWarning(labelOneTime());
      } else {
        makeNewCheckoutBtn();
        checkoutBtn.style.display = "block";
        popUpTriggerBtn.style.display = "none";
      }
    }

    renderProgress(totals.frozen);
    renderSubscriptionSubtotal(totals);
    renameRecommendations();
  };

  /* ---------- subscription subtotal line ---------- */

  // Small line under the cart Subtotal, shown only when the cart has frozen
  // subscription items, so customers can see what counts toward the $125
  // subscription minimum (discounted prices, frozen subscription items only).
  const renderSubscriptionSubtotal = (totals) => {
    const subtotal = document.querySelector('.rebuy-cart .rebuy-cart__flyout-subtotal');
    // The Subtotal sits in a side-by-side row; our line goes on its own row underneath.
    const row = subtotal && subtotal.parentElement;
    if (!row || !row.parentElement) return;
    injectStyles();
    let el = row.parentElement.querySelector(':scope > .pc-sub-subtotal');
    if (!el) {
      el = document.createElement('div');
      el.className = 'pc-sub-subtotal';
      el.innerHTML = '<span>Subscription subtotal</span><span class="pc-sub-subtotal__amt"></span>';
      row.insertAdjacentElement('afterend', el);
    }
    const rowStyle = getComputedStyle(row);
    el.style.paddingLeft = rowStyle.paddingLeft;
    el.style.paddingRight = rowStyle.paddingRight;
    el.style.display = totals.hasSubscription ? '' : 'none';
    const amt = '$' + (totals.frozenSubscription / 100).toFixed(2);
    const amtEl = el.querySelector('.pc-sub-subtotal__amt');
    if (amtEl.textContent !== amt) amtEl.textContent = amt;
  };

  /* ---------- shipping progress bar ---------- */

  const STYLE_ID = 'pc-ship-progress-style';
  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.rebuy-cart .rebuy-cart__progress-step-wrapper,',
      '.rebuy-cart .rebuy-cart__progress-bar-wrapper{display:none !important;}',
      '.rebuy-cart .pc-ship{padding:6px 20px 10px;}',
      '.rebuy-cart .pc-ship__track{background:rgba(121,121,121,0.2);border-radius:999px;height:8px;overflow:hidden;}',
      '.rebuy-cart .pc-ship__fill{background:#4a6e8f;border-radius:inherit;display:block;height:100%;width:0;transition:width .25s ease;}',
      '.rebuy-cart .pc-ship__msg{color:#5b4b4b;font-size:13px;line-height:1.35;margin:6px 0 0;}',
      '.rebuy-cart .pc-sub-subtotal{display:flex;justify-content:space-between;color:#5b4b4b;font-size:13px;line-height:1.35;margin:-4px 0 10px;}'
    ].join('');
    document.head.appendChild(style);
  };

  const renderProgress = (frozen) => {
    const nativeBar = document.querySelector('.rebuy-cart .rebuy-cart__progress-bar-wrapper');
    const host = nativeBar ? nativeBar.parentElement : null;
    if (!host) return;
    injectStyles();
    let el = host.querySelector(':scope > .pc-ship');
    if (!el) {
      el = document.createElement('div');
      el.className = 'pc-ship';
      el.innerHTML =
        '<div class="pc-ship__track" role="progressbar" aria-valuemin="0" aria-valuemax="' + (FREE_SHIPPING / 100) + '" aria-label="Frozen seafood total toward the ' + money(MINIMUM) + ' minimum and ' + money(FREE_SHIPPING) + ' free standard shipping">' +
        '<span class="pc-ship__fill"></span></div>' +
        '<p class="pc-ship__msg" aria-live="polite"></p>';
      host.appendChild(el);
    }
    // Only frozen seafood ships with these rules; hide the bar for carts without any.
    el.style.display = frozen > 0 ? '' : 'none';

    let msg;
    if (frozen < MINIMUM) {
      msg = 'Add ' + money(MINIMUM - frozen) + ' more to meet the ' + money(MINIMUM) + ' minimum.';
    } else if (frozen < FREE_SHIPPING) {
      msg = 'Add ' + money(FREE_SHIPPING - frozen) + ' more for FREE standard shipping, or ship for ' + money(STANDARD_FEE) + '.';
    } else {
      msg = 'You’ve unlocked FREE standard shipping on this order!';
    }
    const pct = Math.min(100, (frozen / FREE_SHIPPING) * 100);
    const fill = el.querySelector('.pc-ship__fill');
    const msgEl = el.querySelector('.pc-ship__msg');
    const track = el.querySelector('.pc-ship__track');
    if (fill.style.width !== pct + '%') fill.style.width = pct + '%';
    if (msgEl.textContent !== msg) msgEl.textContent = msg;
    track.setAttribute('aria-valuenow', (frozen / 100).toFixed(2));
  };

  const renameRecommendations = () => {
    document.querySelectorAll('.rebuy-cart .rebuy-widget .primary-title').forEach(t => {
      if (t.textContent.trim() === 'You may also like') t.textContent = RECS_TITLE;
    });
  };

  // Rebuy re-renders parts of the cart on its own; re-apply our bar and title when it does.
  const watchFlyout = () => {
    const flyout = document.querySelector('.rebuy-cart__flyout');
    if (!flyout || observer) return;
    observer = new MutationObserver(() => {
      if (ensureQueued || !lastCart) return;
      ensureQueued = true;
      requestAnimationFrame(() => {
        ensureQueued = false;
        const t = getTotals(lastCart);
        renderProgress(t.frozen);
        renderSubscriptionSubtotal(t);
        renameRecommendations();
      });
    });
    observer.observe(flyout, { childList: true, subtree: true });
  };

  /* ---------- existing helpers ---------- */

  const accordionSetup = () => {
    accordionToggle = document.querySelector('[data-acc-toggle]');
    if (!accordionToggle) return;
    accordionToggle.addEventListener('click', (event) => {
      event.target.closest('.amb-accordion').classList.toggle("open");
    });
  };

  const limitItemQuantities = () => {
    setTimeout(() => {
      const cartItems = Rebuy.SmartCart.items();
      let minQuantity = 1;
      cartItems.forEach(item => {
        if (item.product?.tags.includes("min-purchase-qty-2")) {
          minQuantity = 2;
        } else {
          minQuantity = 1;
        }
        if (item.quantity < minQuantity && item.line_price !== 0) {
          Rebuy.Cart.setItemQuantity(item, minQuantity);
        }
      });
    }, 300);
  };

  document.addEventListener('rebuy:cart.change', (event) => {
    // delay: the native cart re-renders just before this event fires
    setTimeout(() => {
      updateCheckoutBtn(event.detail?.cart?.cart);
    }, 10);
    limitItemQuantities();
  });

  document.addEventListener('rebuy:smartcart.show', (event) => {
    updateCheckoutBtn(event.detail?.smartcart?.cart);
    watchFlyout();
    if (accordionToggle == null) {
      accordionSetup();
    }
  });

});
